import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('careconnect_token'));

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('careconnect_token');
      if (savedToken) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('careconnect_token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('careconnect_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role: 'owner' });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('careconnect_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('careconnect_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isOwner: user?.role === 'owner',
    isTeamMember: ['va', 'full_admin', 'limited_access'].includes(user?.role),
    isVA: user?.role === 'va',
    isFullAdmin: user?.role === 'full_admin',
    isLimitedAccess: user?.role === 'limited_access',
    isSupport: user?.role === 'support',
    canApprove: user?.role === 'owner' || user?.role === 'full_admin' || user?.role === 'va' || (user?.permissions?.advance_candidates),
    canAccessSettings: user?.role === 'owner' || user?.role === 'support' || user?.role === 'full_admin',
    canManageCandidates: user?.role === 'owner' || user?.role === 'full_admin' || user?.role === 'va' || user?.role === 'limited_access',
    hasPermission: (perm) => {
      if (!user) return false;
      if (user.role === 'owner' || user.role === 'full_admin' || user.role === 'va') return true;
      if (user.role === 'limited_access') return user.permissions?.[perm] || false;
      return false;
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
